import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { Snowflake, Boxes, Eye } from "lucide-react";
import CreativeButton from "../CreativeButton";

type Chip = { icon: React.ReactNode; eyebrow: string; title: string };

const slides: {
  id: number;
  title: string;
  description: string;
  image: string;
  card: string;
  dark: boolean;
  /** White frosted-glass sheen, as on the original banner. */
  frosted?: boolean;
  bubble: string;
  chip: Chip;
}[] = [
  {
    id: 1,
    title: "Keep It Frosty, Keep It Fresh!",
    description: "Discover our cutting-edge freezers designed for ultimate preservation.",
    image: "images/banner1.webp",
    card: "bg-gradient-to-br from-[#4f6fd6] to-[#243ea6]",
    dark: true,
    bubble: "Keep it frosty.",
    chip: { icon: <Snowflake size={16} />, eyebrow: "Chest Freezer", title: "Liebherr" },
  },
  {
    id: 2,
    title: "Revolutionary Freezing Solutions",
    description: "Experience unparalleled efficiency and spacious storage.",
    // Was images/banner2.webp (the blast freezer), hidden for now.
    image: "images/products/efe1102.webp",
    card: "bg-gradient-to-br from-[#C7D2E5] to-[#E6EBF0] border border-white/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]",
    dark: false,
    frosted: true,
    bubble: "Compact & mobile.",
    chip: { icon: <Boxes size={16} />, eyebrow: "Liebherr", title: "EFE 1102" },
  },
  {
    id: 3,
    title: "Your Food, Perfectly Preserved",
    description: "From farm to freezer, ensure lasting freshness with our advanced technology.",
    image: "images/banner3.webp",
    card: "bg-gradient-to-br from-[#f8dca0] to-[#e8b75a]",
    dark: false,
    bubble: "Farm to freezer.",
    chip: { icon: <Eye size={16} />, eyebrow: "Display Freezer", title: "See what's inside" },
  },
];

type BannerSlide = (typeof slides)[number];

const AUTOPLAY_MS = 5500;
const FLIP_SECONDS = 1.3;

// Raised "sticker" look shared by every front-layer tooltip.
const EMBOSS = "shadow-[0_22px_40px_-14px_rgba(0,0,0,0.55)]";

// Every layer is its own plane in 3D space, so each one has to hide its own
// back side — otherwise the front face's pieces show through mirrored when
// the card is turned around.
const layer: React.CSSProperties = { backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" };

// One side of the banner: the flat card at the back, with the product and the
// tooltips floating at increasing distances in front of it.
// `fadeTo` is set on the side that is turning away: its card surface blends
// into the next slide's colour during the first half of the turn, so it
// already matches the side coming into view by the time it goes edge-on.
const SlideFace: React.FC<{
  slide: BannerSlide;
  index: number;
  onQuoteClick: () => void;
  fadeTo?: BannerSlide;
  fadeKey?: number;
}> = ({
  slide,
  index,
  onQuoteClick,
  fadeTo,
  fadeKey,
}) => {
  const text = slide.dark ? "text-white" : "text-slate-900";
  const subText = slide.dark ? "text-white/70" : "text-slate-900/70";

  return (
    <>
      {/* Back layer: the base card with the copy */}
      <div style={layer} className={`absolute inset-x-0 bottom-0 top-[70px] md:top-[80px] rounded-[28px] overflow-hidden ${slide.card}`}>
        {slide.frosted && (
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{ background: "linear-gradient(115deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 32%, rgba(255,255,255,0) 68%, rgba(255,255,255,0.4) 100%)" }}
          />
        )}

        <div className="relative h-full flex flex-col justify-end md:justify-center p-7 md:p-14 md:w-[50%]">
          <p className={`text-xs font-bold tracking-[0.3em] mb-4 ${subText}`}>
            {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </p>
          <h2 className={`text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05] mb-4 ${text}`}>{slide.title}</h2>
          <p className={`text-base md:text-lg mb-7 max-w-md ${subText}`}>{slide.description}</p>
          <div>
            <CreativeButton
              onClick={onQuoteClick}
              fillColor={slide.dark ? "#e1ae48" : "#172b81"}
              hoverTextClassName={slide.dark ? "text-black" : "text-white"}
              className={`font-bold py-3 px-8 focus:outline-none focus:ring-4 focus:ring-white/40 ${slide.dark ? "bg-white text-[#172b81]" : "bg-slate-900 text-white"}`}
            >
              Get Quote
            </CreativeButton>
          </div>
        </div>

        {fadeTo && (
          <motion.div
            key={fadeKey}
            aria-hidden="true"
            className={`absolute inset-0 ${fadeTo.card}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: FLIP_SECONDS * 0.45, ease: "easeInOut" }}
          />
        )}
      </div>

      {/* Middle layer, floating in front of the card. Mobile: above the copy */}
      <div
        className="md:hidden absolute pointer-events-none inset-x-6 top-0 h-[44%]"
        style={{ ...layer, transform: "translateZ(90px)" }}
      >
        <img
          src={slide.image}
          alt={slide.title}
          decoding="async"
          className="w-full h-full object-contain drop-shadow-[0_30px_30px_rgba(0,0,0,0.35)]"
        />
      </div>
      {/* Desktop: the original banner's 550×650 box, centred in the right half,
          lifted 30% above the card and tilted 12° to the right */}
      <div className="hidden md:block absolute inset-x-0 bottom-0 top-[80px] pointer-events-none" style={{ transformStyle: "preserve-3d" }}>
        <div
          className="absolute w-[550px] h-[650px] flex items-center justify-center"
          style={{ ...layer, top: "-30%", left: "75%", transform: "translateX(-50%) translateZ(90px) rotate(12deg)" }}
        >
          <img
            src={slide.image}
            alt={slide.title}
            decoding="async"
            className="w-full h-full object-contain drop-shadow-[0_30px_30px_rgba(0,0,0,0.35)]"
          />
        </div>
      </div>

      {/* Front layer: tooltips, furthest from the card */}
      <div
        className={`absolute flex items-center h-[52px] px-6 rounded-full text-sm font-bold whitespace-nowrap ${EMBOSS} ${
          slide.dark ? "bg-[#e1ae48] text-black" : "bg-[#172b81] text-white"
        } right-6 top-[22%] md:right-[2%] md:top-[26%]`}
        style={{ ...layer, transform: "translateZ(190px)" }}
      >
        {slide.bubble}
      </div>

      <div
        className={`absolute hidden sm:flex items-center gap-3 h-[52px] pl-2 pr-6 rounded-full bg-white ${EMBOSS} md:left-[47%] md:bottom-[30%]`}
        style={{ ...layer, transform: "translateZ(150px)" }}
      >
        <span className="w-9 h-9 rounded-full bg-[#172b81]/10 text-[#172b81] flex items-center justify-center">{slide.chip.icon}</span>
        <span className="text-left leading-tight">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-[#e1ae48]">{slide.chip.eyebrow}</span>
          <span className="block text-sm font-bold text-slate-900">{slide.chip.title}</span>
        </span>
      </div>

      <div
        className={`absolute hidden md:flex items-center gap-3 h-[52px] pl-6 pr-2 rounded-full bg-slate-900 text-white ${EMBOSS} right-[6%] bottom-[12%]`}
        style={{ ...layer, transform: "translateZ(170px)" }}
      >
        <span className="text-left leading-tight">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-[#e1ae48]">RK & K</span>
          <span className="block text-sm font-bold">Our range</span>
        </span>
        <span className="flex -space-x-2">
          {slides.map((s) => (
            <span key={s.id} className="w-9 h-9 rounded-full bg-white border-2 border-slate-900 overflow-hidden flex items-center justify-center">
              <img src={s.image} alt="" aria-hidden="true" className="w-7 h-7 object-contain" />
            </span>
          ))}
        </span>
      </div>
    </>
  );
};

const QuoteCarousel: React.FC<{ onQuoteClick: () => void }> = ({ onQuoteClick }) => {
  // A real two-sided card: side A sits at 0°, side B at 180°. Advancing loads
  // the next slide onto whichever side is facing away, then turns the whole
  // card another half-turn to reveal it.
  const [flips, setFlips] = useState(0);
  const [faceSlides, setFaceSlides] = useState<[number, number]>([0, 1]);
  const [paused, setPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(() => typeof document !== "undefined" && document.hidden);
  const flipping = useRef(false);
  const prefersReducedMotion = useReducedMotion();
  const controls = useAnimationControls();

  const currentSlide = faceSlides[flips % 2];

  // Background tabs keep running timers but freeze animation, so queued slide
  // changes would all replay at once on return. Stop autoplay while hidden.
  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const goToSlide = (target: number) => {
    // One flip at a time, so changes can never stack into a fast spin.
    if (target === currentSlide || flipping.current) return;
    const hiddenSide = (flips + 1) % 2;
    setFaceSlides((prev) => {
      const next: [number, number] = [prev[0], prev[1]];
      next[hiddenSide] = target;
      return next;
    });
    setFlips((f) => f + 1);
  };

  useEffect(() => {
    if (flips === 0) return;
    flipping.current = true;
    controls
      .start(
      prefersReducedMotion
        ? { rotateY: flips * 180, transition: { duration: 0 } }
        : {
            rotateY: flips * 180,
            // Pull back slightly mid-turn so the layers' depth reads clearly.
            scale: [1, 0.86, 1],
            transition: { duration: FLIP_SECONDS, ease: [0.65, 0, 0.35, 1] },
          }
      )
      .then(() => {
        flipping.current = false;
      });
  }, [flips, controls, prefersReducedMotion]);

  useEffect(() => {
    if (paused || tabHidden) return;
    const t = setTimeout(() => goToSlide((currentSlide + 1) % slides.length), AUTOPLAY_MS);
    return () => clearTimeout(t);
    // goToSlide only reads state that is already in this effect's deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, paused, tabHidden]);

  return (
    <motion.section
      id="quote"
      className="relative px-6 py-16 md:px-12 bg-white dark:bg-[#121212] [overflow-x:clip]"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative w-full max-w-[90rem] mx-auto h-[680px] md:h-[540px]" style={{ perspective: 1400 }}>
        <motion.div className="absolute inset-0" style={{ transformStyle: "preserve-3d" }} animate={controls} initial={{ rotateY: 0 }}>
          {faceSlides.map((slideIndex, side) => (
            <div
              key={side}
              className="absolute inset-0"
              style={{ transformStyle: "preserve-3d", transform: side === 1 ? "rotateY(180deg)" : undefined }}
              aria-hidden={side !== flips % 2}
              inert={side !== flips % 2}
            >
              <SlideFace
                slide={slides[slideIndex]}
                index={slideIndex}
                onQuoteClick={onQuoteClick}
                fadeTo={!prefersReducedMotion && flips > 0 && side !== flips % 2 ? slides[currentSlide] : undefined}
                fadeKey={flips}
              />
            </div>
          ))}
        </motion.div>
      </div>

      <div className="flex items-center justify-center gap-2 mt-8">
        {slides.map((s, index) => (
          <button
            key={s.id}
            onClick={() => goToSlide(index)}
            aria-label={`Show slide ${index + 1}`}
            aria-current={index === currentSlide}
            className={`!p-0 !border-0 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide ? "w-8 !bg-[#172b81] dark:!bg-[#e1ae48]" : "w-2 !bg-slate-300 dark:!bg-white/20"
            }`}
          />
        ))}
      </div>
    </motion.section>
  );
};

export default QuoteCarousel;
